import clsx from 'clsx'
import type {FunctionComponent} from 'react'
import {CheckCircledIcon} from '~/icons/about-us-enhanced-page/check-circled-icon'
import {ClimbUpSuccess} from '~/icons/about-us-enhanced-page/climb-up-success'
import {ContactUsCustomer} from '~/icons/about-us-enhanced-page/contact-us-customer'

import {GoodWorkThumbUp} from '~/icons/about-us-enhanced-page/good-work-thumb-up'
import {GraphIcon} from '~/icons/about-us-enhanced-page/graph-icon'
import {InteractionIcon} from '~/icons/about-us-enhanced-page/interaction-icon'
import {SearchIcon} from '~/icons/about-us-enhanced-page/search-icon'
import type {getTranslation} from '~/localization/translation'
import type {DomainsBaseStyles} from '~/types'
import {cloudfrontBaseUrl} from '~/utils/constants'

interface AboutPageEnhancedProps {
  translations: ReturnType<typeof getTranslation>
  domainStyles: DomainsBaseStyles
  contactEmail: string
}

type AboutEnhancedApproachItem = ReturnType<typeof getTranslation>['AboutEnhanced']['approach']['content'][number]
type AboutEnhancedApproachItemId = AboutEnhancedApproachItem['id']
type AboutEnhancedApproachItemIcon = FunctionComponent<{className?: string}>

const approachSectionIconsById: Record<AboutEnhancedApproachItemId, AboutEnhancedApproachItemIcon> = {
  scanning: SearchIcon,
  analysis: GraphIcon,
  identification: CheckCircledIcon,
  learning: InteractionIcon,
}

export const teamImageDesktopUrl = `${cloudfrontBaseUrl}/roundforest-stuff/rf-team-desk.webp`
export const teamImageMobileUrl = `${cloudfrontBaseUrl}/roundforest-stuff/rf-team-mob.webp`

export const AboutPageEnhanced: FunctionComponent<AboutPageEnhancedProps> = ({
  translations,
  domainStyles,
  contactEmail,
}) => {
  return (
    <div className={clsx('relative mx-auto mb-16 mt-10 mobile:px-5', domainStyles.UTILS_MAIN_CONTAINER)}>
      <GoodWorkThumbUp className="absolute -top-5 right-0 z-20 mobile:-top-1" />
      <h1 className="text-base font-medium uppercase tracking-[0.15em] desktop:text-center">
        {translations.AboutEnhanced.title}
      </h1>
      <div className="mt-2 h-[3px] w-14 bg-[#FF9900] desktop:mx-auto" />
      <span className="mt-3 inline-block font-playfair !text-[38px] font-semibold !leading-[52px] desktop:mx-auto desktop:block desktop:w-full desktop:text-center tablet:text-[46px] mobile:w-[270px]">
        {translations.AboutEnhanced.subTitle_betterDecisions}
        <br />
        {translations.AboutEnhanced.subtitle_startingWith}
        <em className="!font-playball !text-[45px] !font-extralight not-italic text-[#FF9901]">
          &nbsp;{translations.AboutEnhanced.subtitle_shopping}&nbsp;
        </em>
        <span className="inline-block">{translations.AboutEnhanced.subtitle_experience}</span>
      </span>
      <p className="mt-6 text-[18px] font-light tracking-[0.15em] desktop:px-5 desktop:text-center mobile:text-base">
        {translations.AboutEnhanced.description_1}
        <br className="desktop:hidden" />
        {translations.AboutEnhanced.weAreHereToChangeIt}
      </p>
      <section className="mt-16 desktop:text-center mobile:mt-8">
        <span className="text-[18px] font-extralight tracking-[0.15em] mobile:text-sm">
          {translations.AboutEnhanced.whoWeAre}
        </span>
        <br />
        <span className="text-[28px] font-semibold tracking-[0.15em] mobile:text-[22px]">
          {translations.AboutEnhanced.meetTheTeam}
        </span>
        <picture className="mt-6 block overflow-hidden rounded-2xl mobile:mt-2 mobile:rounded-lg">
          <source media="(max-width: 767px)" srcSet={teamImageMobileUrl} />
          <img
            src={teamImageDesktopUrl}
            alt={translations.AboutEnhanced.meetTheTeam}
            width={1040}
            height={480}
            fetchpriority="high"
            decoding="async"
            className="h-[480px] w-full object-cover mobile:h-[231px]"
            sizes="(max-width: 767px) 100vw, min(100vw, 1040px)"
          />
        </picture>
        <p className="mt-6 !text-start text-[18px] font-light tracking-[0.15em] mobile:mt-3 mobile:text-base">
          {translations.AboutEnhanced.description_2}
        </p>
      </section>
      <section className="mt-16 flex flex-col space-y-8 desktop:items-center mobile:mt-7 mobile:items-center mobile:justify-center mobile:space-y-4">
        <ClimbUpSuccess className="mx-auto" />
        <div className="h-full border-l-[3px] border-[#FF9900] p-4 desktop:py-2 mobile:ml-5">
          <span className="text-[22px] font-medium leading-6 tracking-[0.1em]">{translations.AboutEnhanced.quote}</span>
        </div>
      </section>
      <section className="mx-auto mt-20 grid w-fit grid-cols-2 justify-items-center gap-10 mobile:mt-8 mobile:w-full mobile:grid-cols-1 mobile:gap-6">
        {translations.AboutEnhanced.stats.map((stat) => (
          <div
            key={stat.id}
            className="flex max-h-[172px] w-full max-w-[300px] flex-col items-center justify-center rounded-2xl px-10 py-8 text-center tracking-widest shadow-[2px_2px_20px_0px_#00000026] mobile:max-w-[340px] mobile:px-4 mobile:py-6"
          >
            <span className="text-xs uppercase text-[#FF9900]">{stat.title}</span>
            <span className="text-[40px]">{stat.value}</span>
            <span className="text-[18px]">{stat.description}</span>
          </div>
        ))}
      </section>

      <section className="mt-20 desktop:!text-center mobile:mt-10">
        <p className="text-[18px] font-extralight tracking-[0.15em] mobile:text-sm">
          {translations.AboutEnhanced.approach.title}
        </p>
        <p className="mt-1 text-[28px] font-bold tracking-[0.15em] mobile:text-[22px]">
          {translations.AboutEnhanced.approach.dataDriven} <br className="desktop:hidden" />{' '}
          {translations.AboutEnhanced.approach.editoriallyIndependent}
        </p>
        <ul className="mt-6 space-y-5 desktop:flex desktop:flex-col desktop:items-center mobile:space-y-4">
          {translations.AboutEnhanced.approach.content.map((item) => {
            const IconComponent = approachSectionIconsById[item.id]

            return (
              <li key={item.id} className="flex flex-col items-start gap-4 mobile:pr-5">
                <span className="mt-1 flex h-[46px] w-[51px] shrink-0 items-center justify-center rounded-xl bg-[#FFF4E3] desktop:mx-auto">
                  <IconComponent />
                </span>
                <p className="text-[18px] leading-[1.4] tracking-[0.11em] mobile:text-base">{item.text}</p>
              </li>
            )
          })}
        </ul>
      </section>
      <section className="mt-20 tracking-[0.15em] desktop:text-center mobile:mt-10">
        <p className="text-[18px] font-extralight mobile:text-sm">{translations.AboutEnhanced.contactUsTitle}</p>
        <p className="mt-1 text-[28px] font-bold mobile:text-[22px]">{translations.AboutEnhanced.contactUsSubtitle}</p>
        <ContactUsCustomer className="mx-auto" />
        <p className="text-base font-normal">
          {translations.AboutEnhanced.contactUsContent_1}&nbsp;
          <a className="font-bold" href={`mailto:${contactEmail}`}>
            {contactEmail}.
          </a>
          <br />
          {translations.AboutEnhanced.contactUsContent_2}
        </p>
        <a
          className="mt-6 flex h-10 w-fit items-center justify-center rounded-[32px] bg-[#FF9900] px-5 text-base font-semibold tracking-wider text-white desktop:mx-auto mobile:mt-4"
          href={`mailto:${contactEmail}`}
        >
          {translations.AboutEnhanced.getInTouch}
        </a>
      </section>
    </div>
  )
}
